import React, { useEffect, useMemo, useState } from "react";
import { api, brl, formatDate } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import BRLInput from "../components/BRLInput";
import {
  RefreshCw, AlertTriangle, CalendarClock, CalendarCheck, MessageCircle,
  CheckCircle2, Search, X, HandCoins, Phone, Bot, Clock, CheckCheck, AlertCircle,
} from "lucide-react";

const STAGE_LABEL = {
  reminder_3d: "Lembrete (D-3)", pre_due: "PIX (D-1)", due: "Vencimento (D)",
  overdue: "Cobrança (em atraso)", sale_updated: "Venda atualizada", manual: "Envio manual",
  confirmed: "Pagamento confirmado",
};
const SEND_STATUS = {
  sent: { c: "text-emerald-600", Icon: CheckCheck, t: "Enviada" },
  scheduled: { c: "text-amber-600", Icon: Clock, t: "Agendada" },
  failed: { c: "text-red-600", Icon: AlertCircle, t: "Falhou" },
};
function daysBetween(dueISO, todayISO) {
  const a = new Date(dueISO + "T00:00:00");
  const b = new Date(todayISO + "T00:00:00");
  return Math.round((a - b) / 86400000);
}
const firstName = (n) => (n || "cliente").split(" ")[0];

export default function RecoveryPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("cobrancas");

  // ---- Cobrancas (installments) ----
  const [insts, setInsts] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [sendsMap, setSendsMap] = useState({});
  const [q, setQ] = useState("");
  const [payModal, setPayModal] = useState(null);
  const [payMethod, setPayMethod] = useState("pix");
  const [confirmPay, setConfirmPay] = useState(false);
  const [paying, setPaying] = useState(false);
  const [logModal, setLogModal] = useState(null);

  // ---- Recuperacao (existing logic, preserved) ----
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [message, setMessage] = useState("");
  const [proposalKey, setProposalKey] = useState("settle_now");

  const loadCob = async () => {
    const [ins, cl, snd] = await Promise.all([
      api.get("/installments"), api.get("/clients"), api.get("/whatsapp/sends"),
    ]);
    setInsts(ins.data);
    const m = {}; cl.data.forEach((c) => { m[c.id] = { name: c.name, phone: c.phone, score: c.score }; });
    setClientsMap(m);
    const sm = {};
    (snd.data || []).forEach((s) => {
      if (!s.installment_id) return;
      (sm[s.installment_id] = sm[s.installment_id] || []).push(s);
    });
    Object.values(sm).forEach((arr) => arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    setSendsMap(sm);
  };
  const loadRec = async () => setItems((await api.get("/recovery/candidates")).data);
  useEffect(() => { loadCob(); loadRec(); }, []);

  const today = new Date().toLocaleDateString("en-CA");
  const { vencidas, hoje, proximas } = useMemo(() => {
    const term = q.trim().toLowerCase();
    const rows = insts
      .filter((i) => i.status !== "paid")
      .map((i) => { const c = clientsMap[i.client_id] || {}; return { ...i, name: c.name || "Cliente", phone: c.phone, dias: daysBetween(i.due_date, today) }; })
      .filter((i) => !term || i.name.toLowerCase().includes(term));
    return {
      vencidas: rows.filter((i) => i.dias < 0).sort((a, b) => a.due_date.localeCompare(b.due_date)),
      hoje: rows.filter((i) => i.dias === 0),
      proximas: rows.filter((i) => i.dias > 0).sort((a, b) => a.due_date.localeCompare(b.due_date)),
    };
  }, [insts, clientsMap, q, today]);

  const sum = (arr) => arr.reduce((a, i) => a + Number(i.open_amount ?? i.amount ?? 0), 0);

  const openPay = async (it) => {
    setPayMethod("pix"); setConfirmPay(false);
    // Fallback seguro: monta o modal com os dados JA carregados na lista (/installments
    // ja retorna balance, paid_total, open_amount, fine, interest, overdue_days). Nao depende
    // de request extra, entao a tela nunca quebra se o settlement estiver indisponivel.
    const updated = Number(it.open_amount ?? it.amount);
    setPayModal({
      ...it,
      principal: Number(it.amount),
      paid_total: Number(it.paid_total || 0),
      balance: Number(it.balance ?? it.amount),
      fine: Number(it.fine || 0),
      interest: Number(it.interest || 0),
      overdue_days: Number(it.overdue_days || 0),
      updated_total: updated,
      received: updated,
      paid_at: new Date().toLocaleDateString("en-CA"),
      note: "",
    });
    // Refresh opcional com o valor mais recente (nunca bloqueia nem exibe erro).
    try {
      const { data } = await api.get(`/installments/${it.id}/settlement`);
      setPayModal((prev) => {
        if (!prev || prev.id !== it.id) return prev;
        const untouched = Number(prev.received) === Number(prev.updated_total);
        return { ...prev, ...data, received: untouched ? Number(data.updated_total) : prev.received };
      });
    } catch { /* usa os dados da lista como fallback */ }
  };
  const doPay = async () => {
    if (!payModal || paying) return;
    const received = Number(payModal.received);
    setPaying(true);
    try {
      await api.post("/payments", {
        installment_id: payModal.id, amount: received, method: payMethod,
        paid_at: payModal.paid_at, note: payModal.note?.trim() || undefined,
      });
      const quitou = received >= Number(payModal.updated_total) - 0.005;
      toast.success(quitou ? "Parcela quitada com sucesso" : "Pagamento parcial registrado");
      setConfirmPay(false); setPayModal(null); await loadCob(); await loadRec();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao registrar pagamento"); }
    finally { setPaying(false); }
  };

  // recovery (unchanged behaviour)
  const openModal = (item, proposal) => {
    setModal({ ...item, chosen: proposal });
    setProposalKey(proposal.key);
    setMessage(`Oi ${firstName(item.name)}, aqui da Cris Lingerie. Notamos um pendente na sua conta. ${proposal.label} Posso te ajudar a regularizar hoje?`);
  };
  const send = async () => {
    if (!modal) return;
    try {
      await api.post(`/recovery/${modal.client_id}/send-proposal`, { proposal_key: proposalKey, message, open_amount: modal.open_amount, days_late: modal.days_late });
      toast.success("Proposta enviada (MOCK WhatsApp)");
      setModal(null);
    } catch { toast.error("Erro"); }
  };
  const reactivate = async (client_id) => {
    try { await api.post(`/recovery/${client_id}/reactivate`); toast.success("Cliente reativada"); loadRec(); loadCob(); }
    catch { toast.error("Erro"); }
  };

  const ChargeCard = ({ it, tone }) => {
    const logs = sendsMap[it.id] || [];
    const partial = it.paid_total > 0;
    const badge = it.dias < 0
      ? { c: "bg-red-100 text-red-700", t: `${Math.abs(it.dias)}d em atraso` }
      : it.dias === 0 ? { c: "bg-amber-100 text-amber-700", t: "Vence hoje" }
      : { c: "bg-emerald-100 text-emerald-700", t: `em ${it.dias}d` };
    const partialChip = it.status === "partial_late"
      ? { c: "bg-orange-100 text-orange-700", t: "Parcial em atraso" }
      : it.status === "partial" ? { c: "bg-blue-100 text-blue-700", t: "Parcialmente paga" } : null;
    return (
      <div className="rounded-2xl border border-border bg-white p-4 shadow-card transition-all hover:shadow-soft" data-testid={`charge-${it.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-semibold ${tone.avatar}`}>{(it.name || "?")[0]?.toUpperCase()}</span>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-brand-dark">{it.name}</div>
              <div className="mt-0.5 text-xs text-[#7A6A78]">Parcela {it.number} · venc. {formatDate(it.due_date)}</div>
              {partialChip && (
                <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${partialChip.c}`} data-testid={`charge-partial-${it.id}`}>{partialChip.t}</span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="tabular text-base font-bold text-brand-dark">{brl(it.open_amount ?? it.amount)}</div>
            {partial && <div className="tabular text-[10px] text-[#9A8A98]">pago {brl(it.paid_total)} · de {brl(it.amount)}</div>}
            <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.c}`}>{badge.t}</span>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button onClick={() => setLogModal({ it, logs })}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-brand-pink-soft bg-brand-pink/5 py-2.5 text-xs font-semibold text-brand transition-colors hover:bg-brand-pink/10"
            data-testid={`charge-automation-${it.id}`}>
            <Bot size={15} /> Cobrança automática
            {logs.length > 0 && <span className="rounded-full bg-brand/10 px-1.5 text-[10px] text-brand">{logs.length}</span>}
          </button>
          {(user?.role === "admin" || user?.role === "reseller") && (
            <button onClick={() => openPay(it)}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-brand-grad py-2.5 text-xs font-semibold text-white transition-transform active:scale-95"
              data-testid={`charge-pay-${it.id}`}>
              <CheckCircle2 size={15} /> Registrar
            </button>
          )}
        </div>
      </div>
    );
  };

  const Bucket = ({ title, icon: Icon, rows, tint, testid }) => (
    <div data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`grid h-8 w-8 place-items-center rounded-xl ${tint.chip}`}><Icon size={17} /></span>
          <h2 className="text-sm font-semibold text-brand-dark">{title}</h2>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-[#7A6A78]">{rows.length}</span>
        </div>
        <div className="tabular text-xs font-semibold text-[#7A6A78]">{brl(sum(rows))}</div>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/60 py-6 text-center text-xs text-[#9A8A98]">Nada aqui.</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {rows.map((it) => <ChargeCard key={it.id} it={it} tone={tint} />)}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="recovery-page" className="space-y-6">
      <div className="topbar">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-brand">Operacional</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-brand-dark">Cobranças</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2">
        <button onClick={() => setTab("cobrancas")} data-testid="tab-cobrancas"
          className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${tab === "cobrancas" ? "bg-brand-grad text-white shadow-[0_4px_12px_rgba(106,27,154,0.28)]" : "border border-border bg-white text-[#7A6A78] hover:bg-muted"}`}>
          Cobranças
        </button>
        <button onClick={() => setTab("recuperacao")} data-testid="tab-recuperacao"
          className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${tab === "recuperacao" ? "bg-brand-grad text-white shadow-[0_4px_12px_rgba(106,27,154,0.28)]" : "border border-border bg-white text-[#7A6A78] hover:bg-muted"}`}>
          Recuperação {items.length > 0 && <span className="ml-1 rounded-full bg-white/25 px-1.5 text-[10px]">{items.length}</span>}
        </button>
      </div>

      {tab === "cobrancas" && (
        <div className="space-y-8" data-testid="cobrancas-tab">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: "v", label: "Vencidas", rows: vencidas, tint: "text-red-600", bar: "bg-red-500" },
              { k: "h", label: "Hoje", rows: hoje, tint: "text-amber-600", bar: "bg-amber-400" },
              { k: "p", label: "Próximas", rows: proximas, tint: "text-emerald-600", bar: "bg-emerald-500" },
            ].map((s) => (
              <div key={s.k} className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-b from-white to-[#FFFAFD] p-4 shadow-card" data-testid={`summary-${s.k}`}>
                <span className={`absolute left-0 top-0 h-full w-1 ${s.bar}`} />
                <div className="text-[11px] font-medium text-[#7A6A78]">{s.label}</div>
                <div className={`mt-1 text-xl font-bold ${s.tint}`}>{s.rows.length}</div>
                <div className="tabular text-[11px] text-[#9A8A98]">{brl(sum(s.rows))}</div>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#9A8A98]" />
            <input data-testid="cobranca-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por cliente..."
              className="h-12 w-full rounded-2xl border border-border bg-white pl-11 pr-4 text-sm shadow-card outline-none focus:border-primary focus:ring-4 focus:ring-primary/10" />
          </div>

          <Bucket title="Vencidas" icon={AlertTriangle} rows={vencidas} testid="bucket-vencidas"
            tint={{ chip: "bg-red-100 text-red-600", avatar: "bg-red-100 text-red-700" }} />
          <Bucket title="Vencem hoje" icon={CalendarClock} rows={hoje} testid="bucket-hoje"
            tint={{ chip: "bg-amber-100 text-amber-600", avatar: "bg-amber-100 text-amber-700" }} />
          <Bucket title="Próximas" icon={CalendarCheck} rows={proximas} testid="bucket-proximas"
            tint={{ chip: "bg-emerald-100 text-emerald-600", avatar: "bg-brand/10 text-brand" }} />

          {vencidas.length + hoje.length + proximas.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border bg-white py-14 text-center text-sm text-[#7A6A78]">Nenhuma cobrança pendente. Tudo em dia! 🎉</div>
          )}
        </div>
      )}

      {tab === "recuperacao" && (
        <div className="space-y-6" data-testid="recuperacao-tab">
          <div className="flex items-start gap-3 rounded-2xl border border-brand-pink-soft bg-brand-pink/5 p-4 text-sm text-brand-dark">
            <HandCoins size={20} className="mt-0.5 shrink-0 text-brand-pink" />
            <div><strong>Como funciona:</strong> propostas automáticas geradas com base no tempo de atraso (10% / 20% / 30% de desconto à vista) ou parcelamento. Após o pagamento, use <em>"Reativar"</em> para limpar o status de inadimplente.</div>
          </div>

          {items.map((it) => (
            <div key={it.client_id} className="rounded-2xl border border-border bg-white p-5 shadow-card sm:p-6" data-testid={`recovery-${it.client_id}`}>
              <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="text-lg font-bold text-brand-dark">{it.name}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#7A6A78]">
                    <Phone size={12} /> {it.phone}<span>·</span><span>Score {it.score}</span>
                    <span>·</span><span className="font-medium text-red-600">{it.days_late} dias em atraso</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[#7A6A78]">Em aberto</div>
                  <div className="tabular text-2xl font-bold text-red-600">{brl(it.open_amount)}</div>
                  <div className="text-xs text-[#7A6A78]">{it.installments_count} parcela(s)</div>
                </div>
              </div>
              <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                {it.proposals.map((p) => (
                  <button key={p.key} onClick={() => openModal(it, p)}
                    className="rounded-2xl border border-border bg-muted/50 p-4 text-left transition-colors hover:border-brand hover:bg-muted"
                    data-testid={`proposal-${it.client_id}-${p.key}`}>
                    <div className="mb-1 text-[10px] uppercase tracking-widest text-brand">{p.title}</div>
                    <div className="text-sm leading-snug text-brand-dark">{p.label}</div>
                  </button>
                ))}
              </div>
              {user?.role === "admin" && (
                <div className="flex items-center justify-end border-t border-border pt-3">
                  <button onClick={() => reactivate(it.client_id)} className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-brand hover:underline" data-testid={`reactivate-${it.client_id}`}>
                    <RefreshCw size={14} /> Marcar como pago + reativar
                  </button>
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && <div className="rounded-2xl border border-dashed border-border bg-white py-12 text-center text-sm text-[#7A6A78]">Nenhum cliente em atraso. Parabéns! 🎉</div>}
        </div>
      )}

      {/* Registrar recebimento modal */}
      {payModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-brand-dark/50 p-6" onClick={() => setPayModal(null)}>
          <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="pay-modal">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-brand">Registrar pagamento</div>
                <h2 className="mt-1 text-lg font-bold text-brand-dark">{payModal.name}</h2>
                <div className="text-xs text-[#7A6A78]">Parcela {payModal.number} · venc. {formatDate(payModal.due_date)}</div>
              </div>
              <button onClick={() => setPayModal(null)} className="grid h-9 w-9 place-items-center rounded-xl text-[#7A6A78] hover:bg-muted"><X size={18} /></button>
            </div>

            {/* Resumo financeiro (sempre saldo atualizado) */}
            <div className="mb-4 space-y-1.5 rounded-2xl border border-border bg-muted/40 p-4">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#7A6A78]">Valor original da parcela</span>
                <span className="tabular font-medium text-brand-dark">{brl(payModal.principal)}</span>
              </div>
              {payModal.overdue_days > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#7A6A78]">Encargos ({payModal.overdue_days}d atraso)</span>
                  <span className="tabular font-medium text-red-600">+ {brl((payModal.fine || 0) + (payModal.interest || 0))}</span>
                </div>
              )}
              {payModal.paid_total > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#7A6A78]">Total já pago</span>
                  <span className="tabular font-medium text-emerald-600">- {brl(payModal.paid_total)}</span>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-border pt-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-[#7A6A78]">Saldo atual</span>
                <span className="tabular text-lg font-bold text-brand-dark" data-testid="pay-saldo-atual">{brl(payModal.updated_total)}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Valor recebido</label>
                <BRLInput value={payModal.received} onChange={(v) => setPayModal({ ...payModal, received: v })} testid="pay-amount-input" />
                <div className="mt-1 flex items-center justify-between text-[11px]">
                  <button type="button" onClick={() => setPayModal({ ...payModal, received: Number(payModal.updated_total) })} className="font-semibold text-brand hover:underline" data-testid="pay-fill-total">Quitar total</button>
                  <span className="text-[#9A8A98]">
                    {Number(payModal.received) >= Number(payModal.updated_total) - 0.005
                      ? "Quita a parcela"
                      : `Saldo restante: ${brl(Math.max(0, Number(payModal.updated_total) - Number(payModal.received || 0)))}`}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Forma de pagamento</label>
                  <select className="input-underline" value={payMethod} onChange={(e) => setPayMethod(e.target.value)} data-testid="pay-method-select">
                    <option value="pix">PIX</option>
                    <option value="cash">Dinheiro</option>
                    <option value="card">Cartão</option>
                    <option value="link">Link de pagamento</option>
                    <option value="boleto">Boleto</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Data do recebimento</label>
                  <input type="date" className="input-underline" value={payModal.paid_at} onChange={(e) => setPayModal({ ...payModal, paid_at: e.target.value })} data-testid="pay-date-input" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Observação (opcional)</label>
                <textarea rows={2} className="w-full rounded-xl border border-border p-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10" value={payModal.note} onChange={(e) => setPayModal({ ...payModal, note: e.target.value })} placeholder="Ex: pagou parte no dinheiro" data-testid="pay-note-input" />
              </div>
              <div className="flex justify-end gap-3 pt-1">
                <button onClick={() => setPayModal(null)} className="text-xs font-semibold uppercase tracking-widest text-[#7A6A78] hover:underline">Cancelar</button>
                <button
                  onClick={() => {
                    const r = Number(payModal.received);
                    if (!(r > 0)) return toast.error("Informe um valor válido");
                    if (r > Number(payModal.updated_total) + 0.005) return toast.error("Valor maior que o saldo atualizado");
                    setConfirmPay(true);
                  }}
                  className="brand" data-testid="pay-continue-btn"><CheckCircle2 size={16} /> Confirmar pagamento</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmacao de recebimento (segunda etapa - evita toques acidentais) */}
      {payModal && confirmPay && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-brand-dark/60 p-6" onClick={() => !paying && setConfirmPay(false)}>
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="pay-confirm-modal">
            <div className="mb-4 flex flex-col items-center text-center">
              <span className="mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
                <CheckCircle2 size={28} />
              </span>
              <h2 className="text-lg font-bold text-brand-dark">Confirmar recebimento</h2>
              <p className="mt-1 text-sm text-[#7A6A78]">Você confirma o recebimento de <strong className="text-emerald-600">{brl(payModal.received)}</strong> desta parcela?</p>
            </div>

            <div className="space-y-2 rounded-2xl border border-border bg-muted/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-[#9A8A98]">Cliente</span>
                <span className="truncate text-sm font-semibold text-brand-dark">{payModal.name}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-[#9A8A98]">Valor recebido</span>
                <span className="tabular text-xl font-bold text-emerald-600" data-testid="pay-confirm-amount">{brl(payModal.received)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-[#9A8A98]">Saldo após</span>
                <span className="tabular text-sm font-semibold text-brand-dark">{brl(Math.max(0, Number(payModal.updated_total) - Number(payModal.received || 0)))}</span>
              </div>
            </div>

            <p className="mt-3 text-center text-xs text-[#9A8A98]">
              {Number(payModal.received) >= Number(payModal.updated_total) - 0.005
                ? "Após confirmar, a parcela será registrada como paga."
                : "Pagamento parcial: a parcela continuará aberta com o saldo restante."}
            </p>

            <div className="mt-5 flex flex-col gap-2">
              <button onClick={doPay} disabled={paying}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white shadow-[0_6px_16px_rgba(5,150,105,0.35)] transition-all hover:bg-emerald-700 active:scale-[0.98] disabled:opacity-60"
                data-testid="pay-confirm-final-btn">
                <CheckCircle2 size={17} /> {paying ? "Registrando..." : "Confirmar pagamento"}
              </button>
              <button onClick={() => setConfirmPay(false)} disabled={paying}
                className="w-full rounded-xl py-2.5 text-xs font-semibold uppercase tracking-widest text-[#7A6A78] hover:bg-muted disabled:opacity-60"
                data-testid="pay-confirm-cancel-btn">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Historico de cobranca automatica */}
      {logModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-brand-dark/50 p-6" onClick={() => setLogModal(null)}>
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="automation-log-modal">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-pink/10 text-brand"><Bot size={20} /></span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-brand">Cobrança automática ativa</div>
                  <h2 className="mt-0.5 text-base font-bold text-brand-dark">{logModal.it.name}</h2>
                  <div className="text-xs text-[#7A6A78]">Parcela {logModal.it.number} · {brl(logModal.it.amount)}</div>
                </div>
              </div>
              <button onClick={() => setLogModal(null)} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[#7A6A78] hover:bg-muted" data-testid="automation-log-close"><X size={18} /></button>
            </div>

            <div className="rounded-2xl border border-brand-pink-soft bg-brand-pink/5 p-3 text-xs leading-relaxed text-brand-dark">
              Esta parcela é cobrada automaticamente pelo sistema (lembrete, PIX e cobranças em atraso). Não é necessário enviar mensagens manuais.
            </div>

            <div className="mt-4 max-h-[45vh] space-y-2 overflow-y-auto" data-testid="automation-log-list">
              {logModal.logs.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border py-8 text-center text-xs text-[#9A8A98]">
                  Nenhuma cobrança automática enviada ainda. As mensagens seguem a régua configurada.
                </div>
              ) : logModal.logs.map((s) => {
                const st = SEND_STATUS[s.status] || { c: "text-[#7A6A78]", Icon: Clock, t: s.status || "—" };
                const when = s.sent_at || s.planned_at || s.created_at;
                return (
                  <div key={s.id} className="rounded-xl border border-border bg-white p-3" data-testid={`automation-log-${s.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-brand-dark">{STAGE_LABEL[s.stage] || s.stage || "Cobrança"}</span>
                      <span className={`flex items-center gap-1 text-[11px] font-semibold ${st.c}`}><st.Icon size={13} /> {st.t}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-[#9A8A98]">
                      <Clock size={11} /> {when ? new Date(when).toLocaleString("pt-BR") : "—"}
                      {s.has_pix && <span className="rounded-full bg-emerald-50 px-1.5 text-emerald-600">PIX anexado</span>}
                    </div>
                    {s.error && <div className="mt-1 text-[11px] text-red-600">{s.error}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Proposta WhatsApp modal (recuperacao) */}
      {modal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-brand-dark/50 p-6" onClick={() => setModal(null)}>
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-pop sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-brand">Proposta para</div>
            <h2 className="mt-1 text-2xl font-bold text-brand-dark">{modal.name}</h2>
            <div className="mt-1 text-xs text-[#7A6A78]">{modal.chosen.title}</div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6}
              className="mt-5 w-full rounded-xl border border-border p-4 text-sm leading-relaxed outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              data-testid="recovery-message-textarea" />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setModal(null)} className="text-xs font-semibold uppercase tracking-widest text-[#7A6A78] hover:underline">Cancelar</button>
              <button onClick={send} className="brand" data-testid="recovery-send-btn"><MessageCircle size={16} /> Enviar WhatsApp</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
